// FieldNoteModal.js
// This is the modal component for the Field Note form
// Uses temporary state pattern - changes are only committed on Save

import React, { useState, useRef } from "react";
// Components
import { SamplingEventDatePicker } from "../../../common/SamplingEventDatePicker";
import { SamplingEventTimePickerInput } from "../../../common/SamplingEventTimePickerInput";
// Styles
import styles from "../FieldNotesTab.module.css";

export const FieldNoteModal = ({
  isAddMode,
  existingNote,
  user,
  onClose,
  onSave,
  fnActions,
}) => {
  // Initialize temp state using the actions
  const [tempState, setTempState] = useState(() => {
    if (!fnActions) return null;
    return fnActions.createInitialNoteTempState(
      isAddMode ? null : existingNote,
      user?.employee_id || "",
      user?.name || ""
    );
  });

  // Ref to prevent double-click saves
  const isSavingRef = useRef(false);

  // Handle updating temp state data
  const handleUpdateField = (field, value) => {
    setTempState((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        [field]: value,
      },
    }));
  };

  // Handle date change from DatePicker
  const handleDateChange = (date) => {
    if (!date) {
      handleUpdateField("noteDate", null);
      return;
    }
    const formattedDate = date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
    handleUpdateField("noteDate", formattedDate);
  };

  // Handle auto-fill date and time
  const handleAutoFillDateTime = () => {
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
    const hours = String(currentDate.getHours()).padStart(2, "0");
    const minutes = String(currentDate.getMinutes()).padStart(2, "0");
    const formattedTime = `${hours}:${minutes}`;

    setTempState((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        noteDate: formattedDate,
        fieldNoteTime: formattedTime,
      },
    }));
  };

  // Handle save - commits temp state
  const handleSave = async () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;

    try {
      await onSave(tempState);
    } finally {
      isSavingRef.current = false;
    }
  };

  // Early return if temp state not initialized
  if (!tempState || !tempState.data) {
    return null;
  }

  const { data } = tempState;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3>{isAddMode ? "Add Field Note" : "Edit Field Note"}</h3>
          <button className={styles.closeButton} onClick={onClose}>
            &times;
          </button>
        </div>
        <div className={styles.modalBody}>
          {/* Date Field */}
          <div className={styles.modalField}>
            <label className={styles.modalLabel}>Date</label>
            <div className={styles.dateTimePickerContainer}>
              <SamplingEventDatePicker
                selected={data.noteDate || ""}
                onChange={handleDateChange}
                dateFormat="MM/dd/yyyy"
                className={styles.modalInput}
                placeholderText="Select date"
                showMonthDropdown
                showYearDropdown
                scrollableYearDropdown
              />
              <button
                className={styles.dateTimeAutoFillButton}
                type="button"
                onClick={handleAutoFillDateTime}
              >
                Auto-fill Date & Time
              </button>
            </div>
          </div>

          {/* Time Field */}
          <div className={styles.modalField}>
            <label className={styles.modalLabel}>Time</label>
            <SamplingEventTimePickerInput
              value={data.fieldNoteTime || ""}
              onChange={(time) => handleUpdateField("fieldNoteTime", time)}
              className={styles.modalInput}
            />
          </div>

          {/* Note Text Field */}
          <div className={styles.modalField}>
            <label className={styles.modalLabel}>Field Note</label>
            <textarea
              className={styles.modalTextarea}
              value={data.fieldNote || ""}
              onChange={(e) => handleUpdateField("fieldNote", e.target.value)}
              placeholder="Enter your notes here..."
              rows={5}
            />
          </div>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button className={styles.saveButton} onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};
